import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { map, reject, trim } from "lodash/fp";

import { getTemporaryTagId } from "@app/analytics/components/TagPopover/utils";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import type { ApiTag, Tag, PatchCustomTagBody } from "@app/common/types";
import { CreateCustomTagBody } from "@app/common/types";
import { replaceWhere } from "@app/utils/arrays";

const EMPTY_TAGS: Tag[] = [];

const useTags = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ["tags", user?.id];

  const refetchTags = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.refetchQueries({ queryKey: ["sessionsList"], exact: false });
  };

  const tagsQuery = useQuery({
    queryKey,
    queryFn: apiClient.tags.getUserTags,
    enabled: Boolean(user),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const createTagsMutation = useMutation({
    mutationFn: (tags: CreateCustomTagBody[]) => {
      return Promise.allSettled(
        map(
          (tag) => apiClient.tags.createTag({ ...tag, name: trim(tag.name) }),
          tags
        )
      );
    },
    onMutate: (newTags) => {
      const tags: ApiTag[] = queryClient.getQueryData(queryKey) ?? [];
      queryClient.setQueryData(queryKey, [
        ...tags,
        ...map(
          (newTag) => ({
            ...newTag,
            kind: "custom",
            id: getTemporaryTagId(),
          }),
          newTags
        ),
      ]);
    },
    onSettled: refetchTags,
  });

  const updateTagMutation = useMutation({
    mutationFn: (tag: Tag) => {
      const body: PatchCustomTagBody = {
        id: tag.id,
        name: trim(tag.name),
        color: tag.color,
        isHidden: tag.isHidden ?? false,
      };
      return apiClient.tags.updateTag(body);
    },
    onMutate: (tag) => {
      const tags: ApiTag[] = queryClient.getQueryData(queryKey) ?? [];
      const updatedQueryData = replaceWhere(tags, tag, (t) => t.id === tag.id);
      queryClient.setQueryData(queryKey, updatedQueryData);
    },
    onSettled: refetchTags,
  });

  const deleteTagMutation = useMutation({
    mutationFn: (id: Tag["id"]) => apiClient.tags.deleteTag(id),
    onMutate: (tagId) => {
      const tags: ApiTag[] = queryClient.getQueryData(queryKey) ?? [];
      queryClient.setQueryData(queryKey, reject({ id: tagId }, tags));
    },
    onSettled: refetchTags,
  });

  return {
    tags: tagsQuery.data ?? EMPTY_TAGS,
    isLoading: tagsQuery.isInitialLoading,
    createTags: createTagsMutation,
    deleteTag: deleteTagMutation,
    updateTag: updateTagMutation,
  };
};

export default useTags;
